import {
  IDENTITY_CONFIG,
  METADATA_OIDC,
  ROLE,
  SECTION,
  ACTION,
} from "./authConst";
// Note: do not import UserManager at module scope to avoid SSR document/window access
import acl from "./acl";
import { Permission } from "role-acl";

export const isBrowser = typeof window !== "undefined";
const LAST_PORTAL_ROUTE_KEY = "lastPortalRoute";
const PORTAL_ENTRY_PATH_COOKIE_KEY = "PortalEntryPath";

const getPortalRootPath = (pathname: string) => {
  const lowerPath = (pathname || "").toLowerCase();
  if (/(^|\/)assessment(\/|$)/.test(lowerPath)) return "/assessment";
  if (/(^|\/)course(\/|$)/.test(lowerPath)) return "/course";
  return undefined;
};

const saveCurrentPortalRoute = (pathname: string) => {
  if (!isBrowser) return;
  const route = getPortalRootPath(pathname);
  if (route) {
    sessionStorage.setItem(LAST_PORTAL_ROUTE_KEY, route);
    localStorage.setItem(LAST_PORTAL_ROUTE_KEY, route);
  }
};

export const getLastPortalRoute = () => {
  if (!isBrowser) return undefined;
  const route =
    sessionStorage.getItem(LAST_PORTAL_ROUTE_KEY) ||
    localStorage.getItem(LAST_PORTAL_ROUTE_KEY);
  return route === "/course" || route === "/assessment" ? route : undefined;
};

export const clearLastPortalRoute = () => {
  if (!isBrowser) return;
  sessionStorage.removeItem(LAST_PORTAL_ROUTE_KEY);
  localStorage.removeItem(LAST_PORTAL_ROUTE_KEY);
};

export const getPortalEntryPathFromCookie = () => {
  if (!isBrowser) return undefined;

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${PORTAL_ENTRY_PATH_COOKIE_KEY}=`));
  console.log("getPortalEntryPathFromCookie cookie", cookie);
  if (!cookie) return undefined;
  console.log("cookie OK");
  const value = decodeURIComponent(
    cookie.substring(PORTAL_ENTRY_PATH_COOKIE_KEY.length + 1),
  );
  console.log("cookie value", value);
  return value.startsWith("/") ? value : undefined;
};

export const clearPortalEntryPathCookie = () => {
  if (!isBrowser) return;
  document.cookie = `${PORTAL_ENTRY_PATH_COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

// Defer loading of oidc-client until runtime in browser
let mgr: any = null;
if (isBrowser) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const oidc = require("oidc-client");
  const UserManager = oidc.UserManager;
  mgr = new UserManager({
    ...IDENTITY_CONFIG,
    metadata: METADATA_OIDC,
  });
}

export const getUser = () => {
  const authority = IDENTITY_CONFIG.authority || process.env.REACT_APP_AUTH_URL;
  const user = isBrowser
    ? sessionStorage.getItem(`oidc.user:${authority}:${process.env.CLIENT_ID}`)
    : null;
  return user;
};

export const getProfile = () => {
  const userJson = getUser();
  return userJson ? JSON.parse(userJson).profile : null;
};

export const getIsRole = (role: ROLE) => {
  return getProfile()?.role === role;
};

export const getIsAdmin = () => {
  return getIsRole(ROLE.Administrator);
};
export const getIsClientAdmin = () => {
  return getIsRole(ROLE.ClientAdministrator);
};
export const getIsDistributor = () => {
  return getIsRole(ROLE.Distributor);
};
export const getIsTechnical = () => {
  return getIsRole(ROLE.Technical);
};

export const can = (
  action: ACTION,
  section: SECTION,
  page: string | undefined = undefined,
  subpage: string | undefined = undefined,
  ...contexts: (string | undefined)[]
) => {
  // console.log("auth-can fun", action, section, page, subpage, contexts);
  const resourceKey = buildResourceKey(section, page, subpage, ...contexts);
  const role = getProfile()?.role ?? ROLE.ClientUser;
  const permission = acl
    .can(role)
    .execute(action)
    .sync()
    .on(resourceKey) as Permission;
  // console.log("auth-can resourceKey", resourceKey);
  // console.log("auth-can permission", permission);
  // console.log("auth-can role", role);
  return permission.granted;
};

export const cannot = (
  action: ACTION,
  section: SECTION,
  page: string | undefined = undefined,
  subpage: string | undefined = undefined,
  ...contexts: (string | undefined)[]
) => {
  return !can(action, section, page, subpage, ...contexts);
};

export const buildResourceKey = (
  section: SECTION,
  page: string | undefined = undefined,
  subpage: string | undefined = undefined,
  ...contexts: (string | undefined)[]
) => {
  return [section, page, subpage, ...contexts]
    .filter((value) => !!value)
    .join("/");
};

export class SectionAuth {
  section: SECTION;
  constructor(section: SECTION) {
    this.section = section;
  }
  can(
    action: ACTION,
    page: string | undefined = undefined,
    subpage: string | undefined = undefined,
    ...contexts: (string | undefined)[]
  ) {
    // console.log("auth-can", action, page, subpage, contexts);
    return (
      can(ACTION.Access, this.section) &&
      can(action, this.section, page, subpage, ...contexts)
    );
  }
  cannot(
    action: ACTION,
    page: string | undefined = undefined,
    subpage: string | undefined = undefined,
    ...contexts: (string | undefined)[]
  ) {
    // console.log("auth-cannot", action, page, subpage, contexts);
    return !this.can(action, page, subpage, ...contexts);
  }
}
export class PageAuth extends SectionAuth {
  page: string | undefined;
  constructor(section: SECTION, page: string | undefined) {
    super(section);
    this.page = page;
  }
  can(
    action: ACTION,
    subpage: string | undefined = undefined,
    ...contexts: (string | undefined)[]
  ) {
    return (
      super.can(ACTION.Access, this.page) &&
      super.can(action, this.page, subpage, ...contexts)
    );
  }
  cannot(
    action: ACTION,
    subpage: string | undefined = undefined,
    ...contexts: (string | undefined)[]
  ) {
    return !this.can(action, subpage, ...contexts);
  }
}

export class SubpageAuth extends PageAuth {
  subpage: string | undefined;
  constructor(
    section: SECTION,
    page: string | undefined,
    subpage: string | undefined,
  ) {
    super(section, page);
    this.subpage = subpage;
  }
  can(action: ACTION, ...contexts: (string | undefined)[]) {
    return (
      super.can(ACTION.Access, this.subpage) &&
      super.can(action, this.subpage, ...contexts)
    );
  }
  cannot(action: ACTION, ...contexts: (string | undefined)[]) {
    return !this.can(action, ...contexts);
  }
}

export const logoutCallback = async () => {
  return await mgr?.signoutRedirectCallback();
};

export const logout = () => {
  console.log("logout isBrowser", isBrowser, window.location.pathname);
  if (isBrowser) {
    saveCurrentPortalRoute(window.location.pathname);
    sessionStorage.removeItem("lang");
    sessionStorage.removeItem("i18NMsg");
  }
  mgr?.signoutRedirect();
};

export const logoutRedirect = () => {
  mgr?.signoutRedirectCallback();
};

export const isLoggedIn = (path: string) => {
  if (
    [
      "/logout-callback/",
      "/login-callback/",
      "/silent-callback/",
      "/register/:passphrase",
    ].includes(path)
  )
    return true;

  const storage = getUser();
  const user = storage ? JSON.parse(storage) : null;
  return !!user && !!user.access_token;
};

export const signinRedirect = (url: string) => {
  mgr?.signinRedirect({ state: url });
};

export const login = (pathname?: string) => {
  if (isBrowser) {
    const targetPath = pathname || window.location.pathname;
    saveCurrentPortalRoute(targetPath);

    const redirectUrl = /^https?:\/\//i.test(targetPath)
      ? targetPath
      : `${window.location.origin}${targetPath.startsWith("/") ? targetPath : `/${targetPath}`
      }`;

    signinRedirect(redirectUrl);
  }
};

export const loginCallback = async () => {
  const result = await mgr?.signinRedirectCallback();
  return result;
};

export const loginSilentCallback = () => {
  try {
    mgr?.signinSilentCallback();
  } catch (err) {
    logout();
  }
};

export const clearStale = () => {
  mgr?.clearStaleState();
};

export { ACTION, ROLE, SECTION, mgr, saveCurrentPortalRoute };
